// Typed wrappers around the bare `axios({...})` calls scattered through the
// stores. See docs/adr/0001-typescript-at-the-api-boundary.md.
//
// Design rules:
//   - Reuse the global `axios` import. Do NOT create a new instance — the
//     response interceptor in data_store.js handles 401s for the whole app
//     and only attaches to the default instance.
//   - Return `Promise<AxiosResponse<T>>`, not `Promise<T>`. Existing consumers
//     check `response.status` and can be migrated piecemeal.
//   - `withCredentials: true` is preserved on mutations to match prior
//     behavior; GETs do not set it.

import axios, { AxiosResponse } from "axios";

import {
  Ack,
  AttendanceAck,
  BillsAck,
  Guest,
  GuestReplayed,
  MealForm,
  MealResident,
} from "../types/api";

// One side of a bills edit: a cook's bill as the page saw it (`from`),
// or as the person wants it (`to`). The amount is text, never a number
// (ADR 0001): "" is 0, and the server refuses a JSON number.
export interface BillValues {
  amount: string;
  no_cost: boolean;
}

// One edit in a bills save (#135, docs/adr/0009-bills-saves-send-edits.md).
// It names one cook. A change or a remove carries the bill the page saw
// for that cook, and the server writes nothing if the stored bill is no
// longer that. A cook no edit names is never touched.
export type BillEdit =
  | { op: "add"; resident_id: number; to: BillValues }
  | { op: "change"; resident_id: number; from: BillValues; to: BillValues }
  | { op: "remove"; resident_id: number; from: BillValues };

// How long the page waits for the answer to a bills save. Heroku's
// router ends a request at 30 seconds, so a request still open after 35
// seconds lost its answer on the way (a dropped connection can leave
// the browser waiting for minutes). Until a save is answered, its row
// takes no edit and its meal is not loaded again, so the wait must end.
export const BILLS_SAVE_TIMEOUT_MS = 35000;

interface SocketBound {
  socketId: string | null;
}

// The version of the person's attendance on the meal that the page
// expects the server to have before this write (public/api.md, "The
// attendance version"). null when the page does not know it: a copy of
// the meal saved on the device before the meal form had versions. The
// request then carries none, and the server does not check it.
interface AttendanceVersioned {
  attendanceVersion: number | null;
}

// A new token for each request from a row of the sign-up list
// (data_store_signup_requests.ts newPushToken). The server puts it in the
// meal's push and sends that push to this page too, so the page can tell
// the push for its own request from any other (#237).
interface PushTokened {
  pushToken: string;
}

function attendanceVersionField(version: number | null) {
  return version === null ? {} : { attendance_version: version };
}

// How long the page waits for the answer to a tap on a row of the
// sign-up list: a sign-up, a take-off, Late, Veg, a guest add or a guest
// removal. The same 35 seconds as a bills save (BILLS_SAVE_TIMEOUT_MS):
// Heroku's router ends a request at 30 seconds, so a request still open
// after 35 seconds lost its answer on the way. Without a limit, the
// browser can wait for minutes on a dropped connection. All that time
// the page shows no message and does not load the meal again, and a
// guest add tapped again goes with a new key, so it can add a second
// guest (S2). When axios stops waiting, its error has no response, and
// the page treats that as no answer (signup_failure.ts).
export const SIGNUP_TIMEOUT_MS = 35000;

export const api = {
  meals: {
    getCooks(mealId: number): Promise<AxiosResponse<MealForm>> {
      return axios.get<MealForm>(`/api/v1/meals/${mealId}/cooks`);
    },

    updateClosed(
      mealId: number,
      { closed, socketId }: { closed: boolean } & SocketBound,
    ): Promise<AxiosResponse<Ack>> {
      return axios({
        method: "patch",
        url: `/api/v1/meals/${mealId}/closed`,
        withCredentials: true,
        data: { closed, socket_id: socketId },
      });
    },

    // The same time limit as a bills save, for the same reason. Until a
    // Cancel or Restore is answered, its button stays disabled (#174).
    updateCancelled(
      mealId: number,
      { cancelled, socketId }: { cancelled: boolean } & SocketBound,
    ): Promise<AxiosResponse<Ack>> {
      return axios({
        method: "patch",
        url: `/api/v1/meals/${mealId}/cancelled`,
        withCredentials: true,
        timeout: BILLS_SAVE_TIMEOUT_MS,
        data: { cancelled, socket_id: socketId },
      });
    },

    updateDescription(
      mealId: number,
      { description, socketId }: { description: string } & SocketBound,
    ): Promise<AxiosResponse<Ack>> {
      return axios({
        method: "patch",
        url: `/api/v1/meals/${mealId}/description`,
        withCredentials: true,
        data: { id: mealId, description, socket_id: socketId },
      });
    },

    // `key` is the save's Idempotency-Key (IETF draft "The
    // Idempotency-Key HTTP Header Field"): a new one for each save, and
    // the same one when that save is sent again. The header's value is a
    // quoted string (RFC 9651). The keys are UUIDs, which have no quote
    // or backslash to escape.
    //
    // `keepalive` is true for a save sent because the page is being
    // hidden or closed (#150). It then goes with fetch and its keepalive
    // flag, which lets the request finish after the page is gone. An
    // XMLHttpRequest, which axios uses otherwise, is ended with the page.
    // It still goes through axios, so the token is added the same way
    // (axios_auth.js). navigator.sendBeacon cannot send the
    // Idempotency-Key or the token, so it is not used.
    updateBills(
      mealId: number,
      {
        edits,
        key,
        socketId,
        keepalive,
      }: { edits: BillEdit[]; key: string; keepalive: boolean } & SocketBound,
    ): Promise<AxiosResponse<BillsAck>> {
      return axios({
        method: "patch",
        url: `/api/v1/meals/${mealId}/bills`,
        withCredentials: true,
        timeout: BILLS_SAVE_TIMEOUT_MS,
        headers: { "Idempotency-Key": `"${key}"` },
        data: { edits, socket_id: socketId },
        ...(keepalive
          ? { adapter: "fetch", fetchOptions: { keepalive: true } }
          : {}),
      });
    },

    updateMax(
      mealId: number,
      { max, socketId }: { max: number | null } & SocketBound,
    ): Promise<AxiosResponse<Ack>> {
      return axios({
        method: "patch",
        url: `/api/v1/meals/${mealId}/max`,
        withCredentials: true,
        data: { max, socket_id: socketId },
      });
    },

    residents: {
      add(
        mealId: number,
        residentId: number,
        {
          late,
          vegetarian,
          socketId,
          attendanceVersion,
          pushToken,
        }: { late: boolean; vegetarian: boolean } & SocketBound &
          AttendanceVersioned &
          PushTokened,
      ): Promise<AxiosResponse<MealResident>> {
        return axios({
          method: "post",
          url: `/api/v1/meals/${mealId}/residents/${residentId}`,
          withCredentials: true,
          timeout: SIGNUP_TIMEOUT_MS,
          data: {
            late,
            vegetarian,
            socket_id: socketId,
            push_token: pushToken,
            ...attendanceVersionField(attendanceVersion),
          },
        });
      },

      remove(
        mealId: number,
        residentId: number,
        {
          socketId,
          attendanceVersion,
          pushToken,
        }: SocketBound & AttendanceVersioned & PushTokened,
      ): Promise<AxiosResponse<AttendanceAck>> {
        return axios({
          method: "delete",
          url: `/api/v1/meals/${mealId}/residents/${residentId}`,
          withCredentials: true,
          timeout: SIGNUP_TIMEOUT_MS,
          data: {
            socket_id: socketId,
            push_token: pushToken,
            ...attendanceVersionField(attendanceVersion),
          },
        });
      },

      // Used for both toggleLate and toggleVeg — the endpoint accepts a partial
      // patch of either flag.
      update(
        mealId: number,
        residentId: number,
        patch: ({ late: boolean } | { vegetarian: boolean }) &
          SocketBound &
          AttendanceVersioned &
          PushTokened,
      ): Promise<AxiosResponse<AttendanceAck>> {
        const { socketId, attendanceVersion, pushToken, ...rest } = patch;
        return axios({
          method: "patch",
          url: `/api/v1/meals/${mealId}/residents/${residentId}`,
          withCredentials: true,
          timeout: SIGNUP_TIMEOUT_MS,
          data: {
            ...rest,
            socket_id: socketId,
            push_token: pushToken,
            ...attendanceVersionField(attendanceVersion),
          },
        });
      },

      guests: {
        // `key` is the add's Idempotency-Key, as for a bills save: a new
        // one for each add, and the same one when a tap sends an add
        // that got no answer again (data_store_guest_adds.ts). The answer
        // is the guest, or, for a key the server has seen with the same
        // host and flag, that add's guest marked "replayed".
        add(
          mealId: number,
          residentId: number,
          {
            vegetarian,
            key,
            socketId,
            pushToken,
          }: { vegetarian: boolean; key: string } & SocketBound & PushTokened,
        ): Promise<AxiosResponse<Guest | GuestReplayed>> {
          return axios({
            method: "post",
            url: `/api/v1/meals/${mealId}/residents/${residentId}/guests`,
            withCredentials: true,
            timeout: SIGNUP_TIMEOUT_MS,
            headers: { "Idempotency-Key": `"${key}"` },
            data: { vegetarian, socket_id: socketId, push_token: pushToken },
          });
        },

        remove(
          mealId: number,
          residentId: number,
          guestId: number,
          { socketId, pushToken }: SocketBound & PushTokened,
        ): Promise<AxiosResponse<Ack>> {
          return axios({
            method: "delete",
            url: `/api/v1/meals/${mealId}/residents/${residentId}/guests/${guestId}`,
            withCredentials: true,
            timeout: SIGNUP_TIMEOUT_MS,
            data: { socket_id: socketId, push_token: pushToken },
          });
        },
      },
    },
  },
};
